import { z } from 'zod';
/** JSON extension values only; bounded to avoid pathological metadata payloads. */
function isJson(value, depth = 0) {
    if (depth > 20)
        return false;
    if (value === null || typeof value === 'string' || typeof value === 'boolean')
        return true;
    if (typeof value === 'number')
        return Number.isFinite(value);
    if (Array.isArray(value))
        return value.every((entry) => isJson(entry, depth + 1));
    if (typeof value !== 'object' ||
        Object.getPrototypeOf(value) !== Object.prototype)
        return false;
    return Object.entries(value).every(([key, entry]) => !['__proto__', 'prototype', 'constructor'].includes(key) &&
        isJson(entry, depth + 1));
}
const OptionalText = z.string().max(1000).nullable().optional();
export const SaleOrderDetailsSchema = z
    .object({
    /** Descriptive ordersHub identity; never a backend payment binding. */
    orderId: z.string().uuid().nullable().optional(),
    orderNumber: OptionalText,
    customerName: OptionalText,
    tableNumber: OptionalText,
    customerType: z.enum(['dine-in', 'to-go']).nullable().optional(),
    orderNotes: z.string().max(10000).nullable().optional(),
    requiresOrderProcessing: z.boolean().nullable().optional(),
    // Business-local date of the daily order number (e.g. 2026-09-27). The
    // legacy Firestore `business/{id}/orders` document id is no longer a known
    // field; old clients that still send it fall through to the catch-all.
    orderDateKey: OptionalText,
})
    .catchall(z
    .unknown()
    .refine((value) => isJson(value), 'Order extensions must be JSON values'))
    .superRefine((value, ctx) => {
    if (Object.keys(value).some((key) => ['__proto__', 'prototype', 'constructor'].includes(key)) ||
        JSON.stringify(value).length > 65536) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid or oversized order details',
        });
    }
});
export const UpdateSaleOrderDetailsRequestSchema = z
    .object({
    orderDetails: SaleOrderDetailsSchema.nullable().optional(),
    expectedRevision: z.number().int().nonnegative().optional(),
})
    .strict();
export const UpdateSaleOrderDetailsResponseSchema = z.object({
    saleId: z.string().uuid(),
    orderDetailsRevision: z.number().int().nonnegative(),
    orderDetails: SaleOrderDetailsSchema.nullable(),
});
/** Absent legacy metadata does not manufacture an order for a normal sale. */
export function getSaleOrderDetails(source) {
    const legacy = source.salesMeta?.order;
    const number = legacy?.number ?? source.details?.orderNumber ?? source.orderNumber;
    const processing = legacy?.requiresProcessing ?? source.details?.requiresOrderProcessing;
    const hasLegacy = Boolean(number) || processing === true;
    const fallback = hasLegacy
        ? {
            ...(number ? { orderNumber: number } : {}),
            ...(processing !== undefined
                ? { requiresOrderProcessing: processing }
                : {}),
            ...(legacy?.customer?.name
                ? { customerName: legacy.customer.name }
                : source.customerInfo && source.customerInfo.name
                    ? { customerName: source.customerInfo.name }
                    : {}),
        }
        : {};
    // Canonical own fields (including null/empty strings) always win.
    return mergeSaleOrderDetails(hasLegacy ? fallback : undefined, source.orderDetails);
}
export function mergeSaleOrderDetails(existing, patch) {
    const entries = Object.entries({
        ...existing,
        ...Object.fromEntries(Object.entries(patch ?? {}).filter(([, value]) => value !== undefined)),
    }).filter(([, value]) => value !== undefined);
    return entries.length
        ? SaleOrderDetailsSchema.parse(Object.fromEntries(entries))
        : undefined;
}
export function withSaleOrderDetails(source) {
    const orderDetails = getSaleOrderDetails(source);
    return orderDetails ? { ...source, orderDetails } : source;
}
/** Compatibility output for clients that still require CheckoutDetails fields. */
export function legacySaleOrderFields(orderDetails) {
    return {
        orderNumber: orderDetails?.orderNumber ?? null,
        requiresOrderProcessing: orderDetails?.requiresOrderProcessing ?? false,
    };
}
/** Compatibility write boundary: old fields may fill missing canonical keys,
 * but may never resurrect a canonical clear or overwrite a newer canonical value. */
export function getSaleOrderDetailsForUpdate(existing, incoming) {
    const legacy = getSaleOrderDetails({ ...incoming, orderDetails: undefined });
    const missing = Object.fromEntries(Object.entries(legacy ?? {}).filter(([key]) => !Object.prototype.hasOwnProperty.call(existing.orderDetails ?? {}, key)));
    return mergeSaleOrderDetails(mergeSaleOrderDetails(getSaleOrderDetails(existing), missing), incoming.orderDetails);
}
